/**
 * Children Page
 * Refactored to use modular components for better maintainability
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Plus, Upload, FileText, Download, AlertTriangle, Rocket, Printer, Clock, GraduationCap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { hasOwnerPermissions, canManageSchools } from "@/utils/permissions";
import AppShell from "@/components/layout/AppShell";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import { LockedButton } from "@/components/FeatureGate";
import { StillHereRemindersDialog } from "@/components/children-records/StillHereRemindersDialog";
import { ManageSchoolsDialog } from "@/components/children-records/ManageSchoolsDialog";

// Import modular components
import {
  ChildForm,
  ChildrenCategoryTab,
  DeletedChildrenTab
} from "@/components/children";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChildrenPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isChildLimitReached, childCount, maxChildren, childSlotsRemaining, subscription, refreshSubscription } = useSubscription();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stillHereDialogOpen, setStillHereDialogOpen] = useState(false);
  const [manageSchoolsOpen, setManageSchoolsOpen] = useState(false);
  const [stillHereChildIds, setStillHereChildIds] = useState(new Set());
  const [schoolsList, setSchoolsList] = useState(null); // null = not yet loaded/failed; [] = loaded, genuinely empty
  const [plannerRooms, setPlannerRooms] = useState([]); // powers the "Room for Ratios" badge below — best-effort, empty if unavailable
  const [pendingApprovals, setPendingApprovals] = useState([]); // [{childId, formId, formName}] — genuine pendingVersion approvals only
  const [editingChild, setEditingChild] = useState(null);
  const [selectedChildren, setSelectedChildren] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    fullTimeSubcategory: '',
    plannerRoomOverride: '',
    school: '',
    birthdate: '',
    startDate: '',
    address: '',
    allergies: '',
    specialDisabilities: '',
    medicalEmergencyInfo: '',
    medicationSpecialConditions: '',
    specialNeedsNotes: '',
    pickupPersons: [{ name: '', phone: '', email: '', relationship: '', restrictions: '', authorizedForRelease: false, address: '' }],
    emergencyContacts: [{ name: '', phone: '' }],
    healthInsurance: { provider: '', policyNumber: '', groupNumber: '', subscriberName: '' },
    physician: { name: '', phone: '', address: '' }
  });
  
  // Check if user can edit (owner, co-owner, test accounts, or staff testing tiers)
  const canEdit = hasOwnerPermissions(user);
  const canEditSchools = canManageSchools(user);
  
  // Scroll position preservation
  const savedScrollPosition = useRef(0);
  const shouldRestoreScroll = useRef(false);
  
  const saveScrollPosition = useCallback(() => {
    savedScrollPosition.current = window.pageYOffset || document.documentElement.scrollTop || 0;
    shouldRestoreScroll.current = true;
  }, []);
  
  const restoreScrollPosition = useCallback(() => {
    if (!shouldRestoreScroll.current) return;
    const targetScroll = savedScrollPosition.current;
    // Use multiple attempts to ensure scroll restoration after React re-renders
    const doScroll = () => window.scrollTo({ top: targetScroll, behavior: 'instant' });
    doScroll();
    requestAnimationFrame(doScroll);
    setTimeout(doScroll, 50);
    setTimeout(doScroll, 100);
    setTimeout(doScroll, 200);
    setTimeout(() => {
      doScroll();
      shouldRestoreScroll.current = false;
    }, 300);
  }, []);
  
  // Effect to restore scroll when dialog closes
  useEffect(() => {
    if (!dialogOpen && shouldRestoreScroll.current) {
      restoreScrollPosition();
    }
  }, [dialogOpen, restoreScrollPosition]);

  // ============== DATA FETCHING ==============
  
  const fetchChildren = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/children`);
      const sortedChildren = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setChildren(sortedChildren);
    } catch (error) {
      console.error("Error fetching children:", error);
      toast.error("Failed to load children");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchChildren();
    axios.get(`${API}/children-records/pending-approvals`)
      .then(r => setPendingApprovals(r.data.pending || []))
      .catch(() => {});
  }, []);

  // Powers the "School Needed" badge below — only computed once this has
  // successfully loaded, so a transient fetch failure never flags every
  // School Age / After School child by mistake.
  const fetchSchoolsList = () => {
    axios.get(`${API}/schools`)
      .then(r => setSchoolsList(r.data || []))
      .catch(() => {});
  };
  useEffect(() => { fetchSchoolsList(); }, []);

  // "Room for Ratios" badge — best-effort: a staff member without Tour Planner access simply
  // gets an empty list, so the badge never renders rather than erroring.
  useEffect(() => {
    axios.get(`${API}/tour-planner/rooms`)
      .then(r => setPlannerRooms(r.data || []))
      .catch(() => setPlannerRooms([]));
  }, []);

  // Still Here Reminders badge — refresh periodically so a badge clears itself
  // once a parent picks the child up, without needing a full page reload.
  useEffect(() => {
    const fetchStillHereStatus = () => {
      axios.get(`${API}/still-here-reminders/status-today`)
        .then(r => setStillHereChildIds(new Set(r.data.childIds || [])))
        .catch(() => {});
    };
    fetchStillHereStatus();
    const interval = setInterval(fetchStillHereStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // ============== FORM HANDLERS ==============
  
  const handleAddPickupPerson = () => {
    setFormData({
      ...formData,
      pickupPersons: [...formData.pickupPersons, { name: '', phone: '', email: '', relationship: '', restrictions: '', authorizedForRelease: false, address: '' }]
    });
  };

  const handleAddAuthorizedPickup = () => {
    const blank = { name: '', phone: '', email: '', relationship: '', restrictions: '', authorizedForRelease: false, address: '' };
    const current = [...formData.pickupPersons];
    if (current.length < 2) current.push({ ...blank }); // silently pad Parent 2 slot
    current.push({ ...blank });
    setFormData({ ...formData, pickupPersons: current });
  };

  const handleRemovePickupPerson = (index) => {
    const newPickupPersons = formData.pickupPersons.filter((_, i) => i !== index);
    setFormData({ ...formData, pickupPersons: newPickupPersons });
  };

  const handlePickupPersonChange = (index, field, value) => {
    const newPickupPersons = [...formData.pickupPersons];
    newPickupPersons[index][field] = value;
    setFormData({ ...formData, pickupPersons: newPickupPersons });
  };

  const handleAddEmergencyContact = () => {
    setFormData({ ...formData, emergencyContacts: [...formData.emergencyContacts, { name: '', phone: '' }] });
  };

  const handleRemoveEmergencyContact = (index) => {
    const newContacts = formData.emergencyContacts.filter((_, i) => i !== index);
    setFormData({ ...formData, emergencyContacts: newContacts });
  };

  const handleEmergencyContactChange = (index, field, value) => {
    const newContacts = [...formData.emergencyContacts];
    newContacts[index][field] = value;
    setFormData({ ...formData, emergencyContacts: newContacts });
  };

  const handleHealthInsuranceChange = (field, value) => {
    setFormData({ ...formData, healthInsurance: { ...formData.healthInsurance, [field]: value } });
  };

  const handlePhysicianChange = (field, value) => {
    setFormData({ ...formData, physician: { ...formData.physician, [field]: value } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Child name is required");
      return;
    }

    // Check child limit before adding new child
    if (!editingChild && isChildLimitReached) {
      toast.error(`Child limit reached (${maxChildren} children). Please upgrade your plan.`);
      navigate('/pricing');
      return;
    }

    // saveScrollPosition is already called when opening the edit modal
    // Don't call it again here to avoid resetting the saved position

    try {
      const validPickupPersons = formData.pickupPersons.filter(p => p.name.trim());
      const dataToSend = {
        ...formData,
        pickupPersons: validPickupPersons
      };

      if (editingChild) {
        await axios.put(`${API}/children/${editingChild.id}`, dataToSend);
        setChildren(prev => prev.map(c => 
          c.id === editingChild.id ? { ...c, ...dataToSend } : c
        ).sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`${formData.name} updated successfully`);
      } else {
        const response = await axios.post(`${API}/children`, dataToSend);
        setChildren(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success(`${formData.name} added successfully`);
        // Refresh subscription to update child count
        refreshSubscription();
      }

      setDialogOpen(false);
      setEditingChild(null);
      setFormData({
        name: '',
        category: '',
        fullTimeSubcategory: '',
        plannerRoomOverride: '',
        school: '',
        birthdate: '',
        startDate: '',
        address: '',
        allergies: '',
        specialDisabilities: '',
        medicalEmergencyInfo: '',
        medicationSpecialConditions: '',
        specialNeedsNotes: '',
        pickupPersons: [{ name: '', phone: '', email: '', relationship: '', restrictions: '', authorizedForRelease: false, address: '' }],
        emergencyContacts: [{ name: '', phone: '' }],
        healthInsurance: { provider: '', policyNumber: '', groupNumber: '', subscriberName: '' },
        physician: { name: '', phone: '', address: '' }
      });
      // Scroll restoration is handled by the useEffect watching dialogOpen
    } catch (error) {
      console.error("Error saving child:", error);
      // Check if it's a plan limit error
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('limit')) {
        toast.error(error.response.data.detail);
        navigate('/pricing');
      } else {
        toast.error("Failed to save child");
      }
    }
  };

  // ============== CHILD ACTIONS ==============
  
  const handleEdit = (child) => {
    saveScrollPosition(); // Save scroll position before opening dialog
    setEditingChild(child);
    setFormData({
      name: child.name || '',
      category: child.category || '',
      fullTimeSubcategory: child.fullTimeSubcategory || '',
      plannerRoomOverride: child.plannerRoomOverride || '',
      school: child.school || '',
      birthdate: child.birthdate || '',
      startDate: child.startDate || '',
      address: child.address || '',
      allergies: child.allergies || '',
      specialDisabilities: child.specialDisabilities || '',
      medicalEmergencyInfo: child.medicalEmergencyInfo || '',
      medicationSpecialConditions: child.medicationSpecialConditions || '',
      specialNeedsNotes: child.specialNeedsNotes || '',
      pickupPersons: child.pickupPersons?.length > 0 
        ? child.pickupPersons.map(p => ({ 
            name: p.name || '', 
            phone: p.phone || '', 
            email: p.email || '', 
            relationship: p.relationship || '',
            restrictions: p.restrictions || '',
            authorizedForRelease: p.authorizedForRelease || false,
            address: p.address || ''
          }))
        : [{ name: '', phone: '', email: '', relationship: '', restrictions: '', authorizedForRelease: false, address: '' }],
      emergencyContacts: child.emergencyContacts?.length > 0
        ? child.emergencyContacts.map(c => ({ name: c.name || '', phone: c.phone || '' }))
        : [{ name: '', phone: '' }],
      healthInsurance: {
        provider: child.healthInsurance?.provider || '',
        policyNumber: child.healthInsurance?.policyNumber || '',
        groupNumber: child.healthInsurance?.groupNumber || '',
        subscriberName: child.healthInsurance?.subscriberName || ''
      },
      physician: {
        name: child.physician?.name || '',
        phone: child.physician?.phone || '',
        address: child.physician?.address || ''
      }
    });
    setDialogOpen(true);
  };

  const handleSoftDelete = async (child) => {
    saveScrollPosition();
    if (window.confirm(`Archive "${child.name}"? They will be moved to the Archived tab.`)) {
      try {
        await axios.put(`${API}/children/${child.id}`, { active: false });
        setChildren(prev => prev.map(c => 
          c.id === child.id ? { ...c, active: false } : c
        ));
        toast.success(`${child.name} moved to Archived`);
        restoreScrollPosition();
      } catch (error) {
        console.error("Error archiving child:", error);
        toast.error("Failed to archive child");
      }
    }
  };

  const handlePermanentDelete = async (child) => {
    saveScrollPosition();
    if (window.confirm(`PERMANENTLY DELETE "${child.name}"?\n\nThis will:\n• Remove all sign-in history\n• Free up 1 child slot\n• Cannot be undone!`)) {
      try {
        await axios.delete(`${API}/children/${child.id}/permanent`);
        setChildren(prev => prev.filter(c => c.id !== child.id));
        toast.success(`${child.name} permanently deleted - slot freed`);
        restoreScrollPosition();
      } catch (error) {
        console.error("Error permanently deleting child:", error);
        toast.error("Failed to permanently delete child");
      }
    }
  };

  const handleReactivate = async (child) => {
    saveScrollPosition();
    try {
      await axios.put(`${API}/children/${child.id}`, { active: true });
      setChildren(prev => prev.map(c => 
        c.id === child.id ? { ...c, active: true } : c
      ));
      toast.success(`${child.name} reactivated`);
      restoreScrollPosition();
    } catch (error) {
      console.error("Error reactivating child:", error);
      toast.error("Failed to reactivate child");
    }
  };

  // Handler for when mismatch is cleared
  const handleMismatchCleared = (childId, extraFields = {}) => {
    setChildren(prev => prev.map(c => 
      c.id === childId ? { ...c, mismatchInfo: null, ...extraFields } : c
    ));
  };

  // ============== SELECTION HANDLERS ==============
  
  const toggleChildSelection = (childId) => {
    setSelectedChildren(prev => {
      const newSet = new Set(prev);
      if (newSet.has(childId)) {
        newSet.delete(childId);
      } else {
        newSet.add(childId);
      }
      return newSet;
    });
  };

  const handleBulkPermanentDelete = async () => {
    if (selectedChildren.size === 0) {
      toast.error("No children selected");
      return;
    }

    const count = selectedChildren.size;
    if (window.confirm(`PERMANENTLY DELETE ${count} CHILDREN?\n\nThis will:\n• Remove all their sign-in history\n• Free up ${count} child slots\n• Cannot be undone!`)) {
      try {
        const response = await axios.post(`${API}/children/bulk-delete`, {
          childIds: Array.from(selectedChildren)
        });
        setChildren(prev => prev.filter(c => !selectedChildren.has(c.id)));
        setSelectedChildren(new Set());
        setSelectMode(false);
        toast.success(`${response.data.deletedCount} children permanently deleted`);
      } catch (error) {
        console.error("Error bulk deleting children:", error);
        toast.error("Failed to delete children");
      }
    }
  };

  const handleBulkRestore = async () => {
    if (selectedChildren.size === 0) {
      toast.error("No children selected");
      return;
    }

    try {
      const childIds = Array.from(selectedChildren);
      await Promise.all(childIds.map(id => 
        axios.put(`${API}/children/${id}`, { active: true })
      ));
      setChildren(prev => prev.map(c => 
        selectedChildren.has(c.id) ? { ...c, active: true } : c
      ));
      setSelectedChildren(new Set());
      setSelectMode(false);
      toast.success(`${childIds.length} children restored`);
    } catch (error) {
      console.error("Error bulk restoring children:", error);
      toast.error("Failed to restore children");
    }
  };

  // ============== EXPORT HANDLER ==============
  
  const handleExportChildren = async () => {
    try {
      const response = await axios.get(`${API}/children/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `children_export_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Children exported successfully');
    } catch (error) {
      console.error("Error exporting children:", error);
      toast.error("Failed to export children");
    }
  };

  // ============== CLEAR ALL ARCHIVED ==============
  
  const handleClearAllArchived = async () => {
    if (archivedChildren.length === 0) {
      toast.error("No archived children to delete");
      return;
    }

    const count = archivedChildren.length;
    const confirmMessage =
      `PERMANENTLY DELETE ALL ${count} ARCHIVED CHILDREN?\n\n` +
      `This will permanently remove:\n` +
      `  • ${count} archived child record(s)\n` +
      `  • ALL of their sign-in history from every past date\n\n` +
      `This CANNOT be undone.\n\n` +
      `Attendance note: Deleting sign-in history will reduce historical\n` +
      `attendance counts for any date those children attended.\n\n` +
      `BACKUP RECOMMENDED: If you ever need to reconcile a past\n` +
      `attendance count, the backup has it. Go to Secure Backup\n` +
      `and download before proceeding.\n\n` +
      `Click OK only if you have backed up or accept the data loss.`;
    if (window.confirm(confirmMessage)) {
      try {
        const response = await axios.delete(`${API}/children/clear-all-archived`);
        setChildren(prev => prev.filter(c => c.active !== false));
        toast.success(`${response.data.deletedCount} archived children permanently deleted`);
      } catch (error) {
        console.error("Error clearing archived children:", error);
        toast.error("Failed to clear archived children");
      }
    }
  };

  // ============== FILTERED DATA ==============
  
  const activeChildren = children.filter(c => c.active !== false);
  const archivedChildren = children.filter(c => c.active === false);
  
  const fullTimeChildren = activeChildren
    .filter(c => c.category === 'full_time' || c.category === 'regular')
    .sort((a, b) => a.name.localeCompare(b.name));
  
  // Full Time subcategory counts
  const fullTimePreK = fullTimeChildren.filter(c => c.fullTimeSubcategory === 'prek');
  const fullTimeOlderToddler = fullTimeChildren.filter(c => c.fullTimeSubcategory === 'older_toddler');
  const fullTimeYoungToddler = fullTimeChildren.filter(c => c.fullTimeSubcategory === 'young_toddler');
  const fullTimeInfant = fullTimeChildren.filter(c => c.fullTimeSubcategory === 'infant');
  const fullTimeNoSubcategory = fullTimeChildren.filter(c => !c.fullTimeSubcategory);
  
  const partTimeChildren = activeChildren
    .filter(c => c.category === 'part_time')
    .sort((a, b) => a.name.localeCompare(b.name));
  const schoolAgeChildren = activeChildren
    .filter(c => c.category === 'school_age')
    .sort((a, b) => a.name.localeCompare(b.name));
  const afterSchoolChildren = activeChildren
    .filter(c => c.category === 'after_school')
    .sort((a, b) => a.name.localeCompare(b.name));
  const uncategorizedChildren = activeChildren
    .filter(c => !c.category || !['full_time', 'part_time', 'school_age', 'after_school', 'regular'].includes(c.category))
    .sort((a, b) => a.name.localeCompare(b.name));

  // ============== RENDER ==============
  
  return (
    <AppShell pageTitle="Manage Children" breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.manageChildren}
        title="Enrolled Children"
        subtitle="View and manage enrolled children profiles"
        altText="Diverse children at daycare"
      />
      
      <div data-testid="children-page" className="relative z-10">
        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-3 items-start">
            <div className="flex flex-col gap-1">
              <div className="flex gap-3">
                <Button
                  onClick={handleExportChildren}
                  variant="outline"
                  className="border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold rounded-xl shadow-md"
                  data-testid="export-children-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Export Children
                </Button>
                <Button
                  onClick={() => window.print()}
                  variant="outline"
                  className="border-green-500 text-green-600 hover:bg-green-50 font-semibold rounded-xl shadow-md print:hidden"
                  data-testid="print-children-btn"
                >
                  <Printer className="w-5 h-5 mr-2" />
                  Print
                </Button>
                {canEdit && (
                  <LockedButton
                    feature="still_here_reminders"
                    onClick={() => setStillHereDialogOpen(true)}
                    className="bg-purple-500 hover:bg-purple-600 text-white font-medium rounded-xl shadow-md print:hidden"
                    data-testid="still-here-reminders-btn"
                  >
                    <Clock className="w-5 h-5 mr-2 text-white" />
                    Still Here / Late Drop Off Reminder
                  </LockedButton>
                )}
                {canEditSchools && (
                  <button
                    onClick={() => setManageSchoolsOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors print:hidden"
                    data-testid="manage-schools-btn"
                  >
                    <GraduationCap className="w-3.5 h-3.5" />
                    Manage Schools
                  </button>
                )}
              </div>
              <p className="text-xs text-blue-600 italic flex items-center gap-1 print:hidden">
                <Printer className="w-3 h-3" />
                To print individual category, select tab above
              </p>
            </div>
          </div>
          {canEdit && (
            <>
              {/* Child Limit Warning */}
              {isChildLimitReached && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4 mb-4 shadow-md">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-bold text-amber-800">Child Limit Reached</p>
                      <p className="text-sm text-amber-700 mt-1">
                        You've reached your plan limit of {maxChildren} children. Upgrade your plan to add more children.
                      </p>
                      <Button 
                        onClick={() => navigate('/pricing')}
                        className="mt-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
                        data-testid="upgrade-plan-btn"
                      >
                        <Rocket className="w-4 h-4 mr-2" />
                        Upgrade Plan
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Slots Remaining Badge */}
              {!isChildLimitReached && maxChildren !== 'unlimited' && (
                <div className="text-sm text-gray-600 mb-2">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                    {childSlotsRemaining} slots remaining ({childCount}/{maxChildren} children)
                  </span>
                </div>
              )}
              
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen} modal={true}>
                <DialogTrigger asChild>
                  <Button
                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold rounded-xl shadow-lg"
                    onClick={() => {
                      if (isChildLimitReached) {
                        toast.error(`Child limit reached (${maxChildren} children). Please upgrade your plan.`);
                        navigate('/pricing');
                        return;
                      }
                      setEditingChild(null);
                      setFormData({
                        name: '',
                        category: 'full_time',
                        fullTimeSubcategory: '',
                        plannerRoomOverride: '',
                        school: '',
                        birthdate: '',
                        startDate: '',
                        address: '',
                        allergies: '',
                        specialDisabilities: '',
                        medicalEmergencyInfo: '',
                        medicationSpecialConditions: '',
                        specialNeedsNotes: '',
                        pickupPersons: [{ name: '', phone: '', email: '', relationship: '', restrictions: '', authorizedForRelease: false, address: '' }],
                        emergencyContacts: [{ name: '', phone: '' }],
                        healthInsurance: { provider: '', policyNumber: '', groupNumber: '', subscriberName: '' },
                        physician: { name: '', phone: '', address: '' }
                      });
                    }}
                    disabled={isChildLimitReached}
                    data-testid="add-child-btn"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Child
                  </Button>
                </DialogTrigger>

                {/* Child Form Dialog */}
                <ChildForm
                  open={dialogOpen}
                  onOpenChange={setDialogOpen}
                  formData={formData}
                  setFormData={setFormData}
                  onSubmit={handleSubmit}
                  editingChild={editingChild}
                  onAddPickupPerson={handleAddPickupPerson}
                  onAddAuthorizedPickup={handleAddAuthorizedPickup}
                  onRemovePickupPerson={handleRemovePickupPerson}
                  onPickupPersonChange={handlePickupPersonChange}
                  onAddEmergencyContact={handleAddEmergencyContact}
                  onRemoveEmergencyContact={handleRemoveEmergencyContact}
                  onEmergencyContactChange={handleEmergencyContactChange}
                  onHealthInsuranceChange={handleHealthInsuranceChange}
                  onPhysicianChange={handlePhysicianChange}
                  organizationId={user?.organizationId}
                  onPhotoUpdate={(childId, photoUrl) => {
                    setChildren(prev => prev.map(c => 
                      c.id === childId ? { ...c, photoUrl } : c
                    ));
                  }}
                />
              </Dialog>
            </>
          )}
          </div>
        
        {/* View-only notice for staff */}
        {!canEdit && (
          <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl text-blue-700 text-sm mb-6">
            <span className="font-medium">View Only</span> - Contact owner to make changes
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <p className="text-center text-[#2F6DB5]">Loading children...</p>
        ) : (
          /* Tabs for Categories */
          <Tabs defaultValue="all" className="w-full">
            {/* Full Time Subcategory Summary - Show at top */}
            <div className="bg-gradient-to-r from-[#2F6DB5]/5 to-[#4DA3D9]/10 rounded-xl p-4 mb-4 border border-[#2F6DB5]/20">
              <div className="flex flex-wrap items-center gap-4 justify-center">
                <span className="font-bold text-[#2F6DB5] text-lg">Total Full Time: {fullTimeChildren.length}</span>
                <span className="text-[#4DA3D9]/50">|</span>
                <span className="text-[#1E293B]">PreK: <span className="font-bold text-[#2F6DB5]">{fullTimePreK.length}</span></span>
                <span className="text-[#1E293B]">Older Toddler: <span className="font-bold text-[#2F6DB5]">{fullTimeOlderToddler.length}</span></span>
                <span className="text-[#1E293B]">Young Toddler: <span className="font-bold text-[#2F6DB5]">{fullTimeYoungToddler.length}</span></span>
                <span className="text-[#1E293B]">Infant: <span className="font-bold text-[#2F6DB5]">{fullTimeInfant.length}</span></span>
              </div>
            </div>
            
            <TabsList className="grid grid-cols-7 gap-2 bg-white/50 p-2 rounded-xl mb-6" data-testid="children-tabs">
              <TabsTrigger value="all" className="rounded-lg data-[state=active]:bg-[#2F6DB5] data-[state=active]:text-white text-xs hover:bg-[#2F6DB5]/10 hover:shadow-md transition-all cursor-pointer border border-transparent hover:border-[#2F6DB5]/30">
                Total Enrolled ({activeChildren.length})
              </TabsTrigger>
              <TabsTrigger value="full_time" className="rounded-lg data-[state=active]:bg-[#2F6DB5] data-[state=active]:text-white text-xs hover:bg-[#4DA3D9]/10 hover:shadow-md transition-all cursor-pointer border border-transparent hover:border-[#4DA3D9]/30">
                Full Time ({fullTimeChildren.length})
              </TabsTrigger>
              <TabsTrigger value="part_time" className="rounded-lg data-[state=active]:bg-[#2F6DB5] data-[state=active]:text-white text-xs hover:bg-[#E83E8C]/10 hover:shadow-md transition-all cursor-pointer border border-transparent hover:border-[#E83E8C]/30">
                Part Time ({partTimeChildren.length})
              </TabsTrigger>
              <TabsTrigger value="school_age" className="rounded-lg data-[state=active]:bg-[#2F6DB5] data-[state=active]:text-white text-xs hover:bg-[#F4A623]/10 hover:shadow-md transition-all cursor-pointer border border-transparent hover:border-[#F4A623]/30">
                School Age ({schoolAgeChildren.length})
              </TabsTrigger>
              <TabsTrigger value="after_school" className="rounded-lg data-[state=active]:bg-[#2F6DB5] data-[state=active]:text-white text-xs hover:bg-[#4DA3D9]/10 hover:shadow-md transition-all cursor-pointer border border-transparent hover:border-[#4DA3D9]/30">
                After School ({afterSchoolChildren.length})
              </TabsTrigger>
              <TabsTrigger value="uncategorized" className="rounded-lg data-[state=active]:bg-[#2F6DB5] data-[state=active]:text-white text-xs hover:bg-[#64748B]/10 hover:shadow-md transition-all cursor-pointer border border-transparent hover:border-[#64748B]/30">
                Uncategorized ({uncategorizedChildren.length})
              </TabsTrigger>
              <TabsTrigger value="archived" className="rounded-lg data-[state=active]:bg-[#E83E8C] data-[state=active]:text-white text-[#E83E8C] text-xs hover:bg-[#E83E8C]/10 hover:shadow-md transition-all cursor-pointer border border-transparent hover:border-[#E83E8C]/30">
                Archived ({archivedChildren.length})
              </TabsTrigger>
            </TabsList>

            {/* All Children Tab */}
            <TabsContent value="all" data-testid="all-children-tab">
              <ChildrenCategoryTab
                children={activeChildren}
                category="all"
                onEdit={handleEdit}
                onDelete={handleSoftDelete}
                onMismatchCleared={handleMismatchCleared}
                selectMode={selectMode}
                selectedChildren={selectedChildren}
                onToggleSelect={toggleChildSelection}
                canEdit={canEdit}
                pendingApprovals={pendingApprovals}
                stillHereChildIds={stillHereChildIds}
                schoolsList={schoolsList}
                plannerRooms={plannerRooms}
              />
            </TabsContent>

            {/* Full Time Tab */}
            <TabsContent value="full_time" data-testid="full-time-children-tab">
              <ChildrenCategoryTab
                children={fullTimeChildren}
                category="full_time"
                onEdit={handleEdit}
                onDelete={handleSoftDelete}
                onMismatchCleared={handleMismatchCleared}
                selectMode={selectMode}
                selectedChildren={selectedChildren}
                onToggleSelect={toggleChildSelection}
                canEdit={canEdit}
                pendingApprovals={pendingApprovals}
                stillHereChildIds={stillHereChildIds}
                schoolsList={schoolsList}
                plannerRooms={plannerRooms}
              />
            </TabsContent>

            {/* Part Time Tab */}
            <TabsContent value="part_time" data-testid="part-time-children-tab">
              <ChildrenCategoryTab
                children={partTimeChildren}
                category="part_time"
                onEdit={handleEdit}
                onDelete={handleSoftDelete}
                onMismatchCleared={handleMismatchCleared}
                selectMode={selectMode}
                selectedChildren={selectedChildren}
                onToggleSelect={toggleChildSelection}
                canEdit={canEdit}
                pendingApprovals={pendingApprovals}
                stillHereChildIds={stillHereChildIds}
                schoolsList={schoolsList}
                plannerRooms={plannerRooms}
              />
            </TabsContent>

            {/* School Age Tab */}
            <TabsContent value="school_age" data-testid="school-age-children-tab">
              <ChildrenCategoryTab
                children={schoolAgeChildren}
                category="school_age"
                onEdit={handleEdit}
                onDelete={handleSoftDelete}
                onMismatchCleared={handleMismatchCleared}
                selectMode={selectMode}
                selectedChildren={selectedChildren}
                onToggleSelect={toggleChildSelection}
                canEdit={canEdit}
                pendingApprovals={pendingApprovals}
                stillHereChildIds={stillHereChildIds}
                schoolsList={schoolsList}
                plannerRooms={plannerRooms}
              />
            </TabsContent>

            {/* After School Tab */}
            <TabsContent value="after_school" data-testid="after-school-children-tab">
              <ChildrenCategoryTab
                children={afterSchoolChildren}
                category="after_school"
                onEdit={handleEdit}
                onDelete={handleSoftDelete}
                onMismatchCleared={handleMismatchCleared}
                selectMode={selectMode}
                selectedChildren={selectedChildren}
                onToggleSelect={toggleChildSelection}
                canEdit={canEdit}
                pendingApprovals={pendingApprovals}
                stillHereChildIds={stillHereChildIds}
                schoolsList={schoolsList}
                plannerRooms={plannerRooms}
              />
            </TabsContent>

            {/* Uncategorized Tab */}
            <TabsContent value="uncategorized" data-testid="uncategorized-children-tab">
              <ChildrenCategoryTab
                children={uncategorizedChildren}
                category="uncategorized"
                onEdit={handleEdit}
                onDelete={handleSoftDelete}
                onMismatchCleared={handleMismatchCleared}
                selectMode={selectMode}
                selectedChildren={selectedChildren}
                onToggleSelect={toggleChildSelection}
                canEdit={canEdit}
                pendingApprovals={pendingApprovals}
                stillHereChildIds={stillHereChildIds}
                schoolsList={schoolsList}
                plannerRooms={plannerRooms}
              />
            </TabsContent>

            {/* Archived Tab */}
            <TabsContent value="archived" data-testid="archived-children-tab">
              <DeletedChildrenTab
                children={archivedChildren}
                onRestore={handleReactivate}
                onPermanentDelete={handlePermanentDelete}
                selectMode={selectMode}
                selectedChildren={selectedChildren}
                onToggleSelect={toggleChildSelection}
                onBulkRestore={handleBulkRestore}
                onBulkPermanentDelete={handleBulkPermanentDelete}
                selectedCount={selectedChildren.size}
                onClearAll={handleClearAllArchived}
                tabLabel="Archived"
                canEdit={canEdit}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
      {canEdit && (
        <StillHereRemindersDialog
          open={stillHereDialogOpen}
          onOpenChange={setStillHereDialogOpen}
          children={children}
        />
      )}
      {canEditSchools && (
        <ManageSchoolsDialog
          open={manageSchoolsOpen}
          onOpenChange={setManageSchoolsOpen}
          canEdit={true}
          onSchoolsChanged={fetchSchoolsList}
        />
      )}
    </AppShell>
  );
};

export default ChildrenPage;
